import {Page} from '@PosterWhiteboard/page/page.class';
import type {PosterPageObject} from '@PosterWhiteboard/poster/poster.types';
import type {Poster} from '@PosterWhiteboard/poster/poster.class';
import type {PageObject} from '@PosterWhiteboard/page/page.types';
import type {ItemType} from '@PosterWhiteboard/items/item/item.types';
import type {UpdateFromObjectOpts} from '@PosterWhiteboard/common.types';
import type {DeepPartial} from '@/global';
import {noop} from '@Utils/general.util';
import {updateInnerPageContainerSpacing} from '@Components/poster-editor/library/poster-editor-library';

interface AddPageOpts {
  undoable?: boolean;
  updateRedux?: boolean;
  pageOrder?: number;
  onItemAddFail?: () => void;
}

export class PosterPages {
  public pagesHashMap: Record<string, Page> = {};
  public pageOrder: string[] = [];
  public poster: Poster;

  public constructor(poster: Poster) {
    this.poster = poster;
    this.poster.on('time:updated', this.onPageTimeUpdated.bind(this));
  }

  public onPageTimeUpdated(time: number): void {
    this.updateSelectedPageForTime(time);
  }

  public async addPage(pageObject: DeepPartial<PageObject> = {}, {updateRedux = true, undoable = true, pageOrder, onItemAddFail = noop}: AddPageOpts = {}): Promise<Page> {
    const page = new Page(this.poster, pageObject.hashedID);
    page.applyScale();
    this.pagesHashMap[page.hashedID] = page;
    // TODO add possible race for initial mutliple pages not loading in order
    if (this.pageOrder.indexOf(page.hashedID) === -1) {
      if (pageOrder !== undefined && pageOrder < this.pageOrder.length) {
        const newPageOrder = [...this.pageOrder];
        newPageOrder.splice(pageOrder, 0, page.hashedID);
        this.pageOrder = newPageOrder;
      } else {
        this.pageOrder = [...this.pageOrder, page.hashedID];
      }
    }
    if (page.poster.getCurrentPageId() === '') {
      page.poster.setCurrentPage(page.hashedID);
    }
    // page.poster.updatePagesVisibility();

    await page.updateFromObject(pageObject, {undoable: false, updateRedux: false, onItemAddFail});

    this.initSelectionEvents(page);

    if (undoable) {
      this.poster.history.addPosterHistory();
    }
    if (updateRedux) {
      this.poster.redux.updateReduxData();
    }
    this.refreshPagesClassesForMargin();
    return page;
  }

  private initSelectionEvents(newlyAddedPage: Page): void {
    newlyAddedPage.fabricCanvas.on('selection:created', () => {
      for (const page of Object.values(this.pagesHashMap)) {
        if (page.hashedID !== newlyAddedPage.hashedID) {
          page.clearSelection();
        }
      }
    });

    newlyAddedPage.fabricCanvas.on('mouse:down', () => {
      for (const page of Object.values(this.pagesHashMap)) {
        if (page.hashedID !== newlyAddedPage.hashedID) {
          page.clearSelection();
        }
      }
    });

    newlyAddedPage.fabricCanvas.on('selection:updated', () => {
      for (const page of Object.values(this.pagesHashMap)) {
        if (page.hashedID !== newlyAddedPage.hashedID) {
          page.clearSelection();
        }
      }
    });
  }

  public movePageUp(pageId: string): void {
    this.movePageToIndex(pageId, this.pageOrder.indexOf(pageId) - 1);
  }

  public movePageDown(pageId: string): void {
    this.movePageToIndex(pageId, this.pageOrder.indexOf(pageId) + 1);
  }

  public movePageToIndex(pageId: string, newIndex: number): void {
    const newPagesOrder = [...this.pageOrder];
    const oldIndex = this.pageOrder.indexOf(pageId);
    newPagesOrder.splice(oldIndex, 1);
    newPagesOrder.splice(newIndex, 0, pageId);

    this.pageOrder = newPagesOrder;
    this.refreshPagesHtmlOrder();
    this.poster.refreshAllVisibleCanvases();
    this.refreshPagesClassesForMargin();
    void this.seekToPageIndex(newIndex);

    this.poster.history.addPosterHistory();
    this.poster.redux.updateReduxData();
  }

  public toObject(): PosterPageObject {
    const pageObjects: Record<string, PageObject> = {};

    for (const [key, page] of Object.entries(this.pagesHashMap)) {
      pageObjects[key] = page.toObject();
    }

    return {
      pagesHashMap: pageObjects,
      pageOrder: this.pageOrder,
    };
  }

  public getTotalDuration(): number {
    let totalDuration = 0;
    for (const [, page] of Object.entries(this.pagesHashMap)) {
      totalDuration += page.getDuration();
    }
    return totalDuration;
  }

  public refreshPagesClassesForMargin(): void {
    for (let i = 0; i < this.pageOrder.length; i++) {
      updateInnerPageContainerSpacing(this.pageOrder[i], i, this.pageOrder.length);
    }
  }

  public refreshPagesHtmlOrder(): void {
    const parent = document.querySelector('.pmw-page-container-parent');

    if (parent) {
      const containerMap = new Map<string, HTMLElement>();

      const containers = parent.querySelectorAll<HTMLElement>('.pmw-page-container');
      containers.forEach(container => {
        const dataId = container.getAttribute('data-id');
        if (dataId) {
          containerMap.set(dataId, container);
        }
      });

      this.pageOrder.forEach(id => {
        const el = containerMap.get(id);
        if (el) {
          parent.appendChild(el);
        }
      });
    }
  }

  public async updateFromObject(
    posterPagesObject: DeepPartial<PosterPageObject>,
    {updateRedux = true, fireEvent = true, undoable = true, refreshActiveSelection = false, checkForDurationUpdate = false, onItemAddFail = noop}: UpdateFromObjectOpts = {}
  ): Promise<void> {
    if (!posterPagesObject.pagesHashMap) {
      return;
    }

    // Update/Add pages from pagesObject
    const promises = [];
    let doesPagesRequireSpacingRefresh = false;
    for (const [hashId, pageObject] of Object.entries(posterPagesObject.pagesHashMap)) {
      if (this.pagesHashMap[hashId] !== undefined) {
        promises.push(
          this.pagesHashMap[hashId].updateFromObject(pageObject, {
            updateRedux: false,
            fireEvent: false,
            undoable: false,
            refreshActiveSelection,
            onItemAddFail,
            checkForDurationUpdate,
          })
        );
      } else {
        promises.push(this.addPage(pageObject, {updateRedux: false, undoable: false, pageOrder: posterPagesObject.pageOrder?.indexOf(hashId), onItemAddFail}));
        doesPagesRequireSpacingRefresh = true;
      }
    }

    // delete pages that are in the poster but not in the pagesObject
    for (const [hashId] of Object.entries(this.pagesHashMap)) {
      if (posterPagesObject.pagesHashMap[hashId] === undefined) {
        promises.push(this.doRemovePage(hashId));
        doesPagesRequireSpacingRefresh = true;
      }
    }
    await Promise.all(promises);

    // just in case it is not updated in add/remove page and only the page order has been changed.
    if (posterPagesObject.pageOrder) {
      this.pageOrder = [...posterPagesObject.pageOrder];
      doesPagesRequireSpacingRefresh = true;
    }

    if (undoable) {
      this.poster.history.addPosterHistory();
    }
    if (updateRedux) {
      this.poster.redux.updateReduxData();
    }
    if (fireEvent) {
      this.poster.fire('modified');
    }

    if (doesPagesRequireSpacingRefresh) {
      this.refreshPagesClassesForMargin();
    }
  }

  public async removePage(pageUid: string): Promise<void> {
    if (this.hasPage(pageUid)) {
      await this.doRemovePage(pageUid);
      this.poster.history.addPosterHistory();
      this.poster.redux.updateReduxData();
      this.poster.refreshAllVisibleCanvases();
      this.refreshPagesClassesForMargin();
    }
  }

  public hasPage(hashId: string): boolean {
    return this.pagesHashMap[hashId] !== undefined;
  }

  public getPage(hashId: string): Page | undefined {
    if (!this.hasPage(hashId)) {
      return undefined;
    }

    return this.pagesHashMap[hashId];
  }

  public applyScales(): void {
    for (const [, page] of Object.entries(this.pagesHashMap)) {
      page.applyScale();
    }
  }

  public convertTransparentBackgroundsToSolid(): void {
    for (const [, page] of Object.entries(this.pagesHashMap)) {
      if (page.background.isTransparentBackground()) {
        page.background.updateBackgroundToBeNonTransparent();
      }
    }
  }

  public getItemForId(uid: string): ItemType | undefined {
    for (const [, page] of Object.entries(this.pagesHashMap)) {
      if (page.items.hasItem(uid)) {
        return page.items.getItem(uid);
      }
    }
    return undefined;
  }

  public getPageWithItemId(uid: string): Page | undefined {
    for (const [, page] of Object.entries(this.pagesHashMap)) {
      if (page.items.hasItem(uid)) {
        return page;
      }
    }
    return undefined;
  }

  public hasMultiplePages(): boolean {
    return Object.keys(this.pagesHashMap).length > 1;
  }

  public hasVideoPage(): boolean {
    for (const [, page] of Object.entries(this.pagesHashMap)) {
      if (page.isVideo()) {
        return true;
      }
    }

    return false;
  }

  public isPremium(): boolean {
    for (const [, page] of Object.entries(this.pagesHashMap)) {
      if (page.isPremium()) {
        return true;
      }
    }

    return false;
  }

  public clearSelection(): void {
    for (const [, page] of Object.entries(this.pagesHashMap)) {
      page.clearSelection();
    }
  }

  public disposeCanvas(): void {
    for (const [, page] of Object.entries(this.pagesHashMap)) {
      page.clearSelection();
      page.dispose();
    }
  }

  private async doRemovePage(hashId: string): Promise<void> {
    if (this.hasPage(hashId)) {
      this.pagesHashMap[hashId].clearSelection();
      await this.pagesHashMap[hashId].dispose();
      this.pagesHashMap[hashId].removeHtml();
      const newCurrentPageIdIfCurrentPageDeleted = this.getAdjacentPageIdOnPageRemoved(this.poster.getCurrentPageId());
      // this.page.fabricCanvas.remove(this.pagesHashMap[itemUid].fabricObject);
      delete this.pagesHashMap[hashId];
      this.pageOrder = this.pageOrder.filter((id) => {
        return id !== hashId;
      });
      if (this.poster.getCurrentPageId() === hashId) {
        this.poster.setSelectedPageAndUpdateView(newCurrentPageIdIfCurrentPageDeleted);
        const newCurrentPageOrder = this.pageOrder.indexOf(newCurrentPageIdIfCurrentPageDeleted);
        void this.poster.pages.seekToPageIndex(newCurrentPageOrder);
      }
    }
  }

  public getAdjacentPageIdOnPageRemoved(hashId: string): string {
    const pageOrder = this.pageOrder.indexOf(hashId);
    const adjacentPageOrder = pageOrder === 0 ? 1 : pageOrder - 1;
    return this.pageOrder[adjacentPageOrder];
  }

  public getPageOrder(pageHashId: string): number | undefined {
    const pageOrder = this.pageOrder.indexOf(pageHashId);
    return pageOrder !== -1 ? pageOrder : undefined;
  }

  public getPagesInOrder(): Page[] {
    const pages = [];
    for (const pageId of this.pageOrder) {
      pages.push(this.pagesHashMap[pageId]);
    }

    return pages;
  }

  public getNumberOfPages(): number {
    return Object.keys(this.pagesHashMap).length;
  }

  public showPage(pageId: string): void {
    const pageElement = Page.getHTMLContainerForPageId(pageId);
    pageElement?.classList.remove('_hidden');
  }

  public hidePage(pageId: string): void {
    const pageElement = Page.getHTMLContainerForPageId(pageId);
    pageElement?.classList.add('_hidden');
  }

  public getPreviousPageIndex(): number | undefined {
    const previousSlideIndex = (this.getPageOrder(this.poster.getCurrentPageId()) ?? 0) - 1;
    return previousSlideIndex >= 0 ? previousSlideIndex : undefined;
  }

  public getNextPageIndex(): number | undefined {
    const nextPageIndex = (this.getPageOrder(this.poster.getCurrentPageId()) ?? 0) + 1;
    return nextPageIndex < Object.keys(this.pagesHashMap).length ? nextPageIndex : undefined;
  }

  public async seekToPageIndex(pageIndex: number): Promise<void> {
    if (pageIndex !== undefined) {
      // TODO: See if we need to create a new function for start transitioned time & use that instead.
      const pageTime = this.getStartTimeForPage(this.pagesHashMap[this.pageOrder[pageIndex]].hashedID);
      if (pageTime !== undefined) {
        await this.poster.seek(pageTime);
      }
    }
  }

  public getStartTimeForPage(pageId: string): number | undefined {
    let startTime = 0;
    for (const id of this.pageOrder) {
      if (pageId === id) {
        return startTime;
      }
      startTime += this.pagesHashMap[id].getDuration();
    }
    return undefined;
  }

  public getEndTimeForPage(pageId: string): number | undefined {
    const pageIndex = this.pageOrder.indexOf(pageId);
    const startTimeForPage = this.getStartTimeForPage(pageId);
    if (startTimeForPage !== undefined && pageIndex !== undefined) {
      return startTimeForPage + this.pagesHashMap[this.pageOrder[pageIndex]].getDuration();
    }
    return undefined;
  }

  protected getPageIndexForTime(time: number): number {
    const numberOfPages = Object.keys(this.pagesHashMap).length;
    const posterDuration = this.poster.getDuration();
    let posterTime = 0;

    let index = 0;

    for (const id of this.pageOrder) {
      const page = this.pagesHashMap[id];
      posterTime += page.getDuration();
      if (posterTime > time) {
        return index;
      }
      if (time >= posterDuration) {
        return numberOfPages - 1;
      }
      index += 1;
    }
    return index;
  }

  public updateSelectedPageForTime(time: number): void {
    const pageIndex = this.getPageIndexForTime(time);
    const page = this.pagesHashMap[this.pageOrder[pageIndex]];
    const currentPage = this.poster.getCurrentPage();

    if (page && currentPage.hashedID !== page.hashedID) {
      currentPage.clearSelection();
      this.switchToPage(page.hashedID);
    }
  }

  public switchToPage(pageId: string): void {
    this.poster.setSelectedPageAndUpdateView(pageId);
    void this.pagesHashMap[pageId].playPage();
  }
}
